import { Box, Button } from '@mui/material';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, TrashIcon } from '~/assets/icons';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import ContactList from '~/components/contact/ContactList/ContactList';
import EditCustomerDialog from '~/components/customer/edit-customer-dialog/EditCustomerDialog';
import usePermission from '~/hooks/usePermission';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { customerActions } from '~/thunks/customer/customerSlice';
import { handleGetContacts, handleGetDetail } from '~/thunks/customer/customerThunk';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { PERMISSIONS } from '~/utils/constants/permission';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { ICustomer, IPayloadDelete } from '~/utils/interface/customer';
import styles from './Contact.module.scss';
import classNames from 'classnames/bind';
import jobApi from '~/apis/job';

const cx = classNames.bind(styles);

type ContactProps = {
  id: string;
};

const Contact = (props: ContactProps) => {
  //#region Destructuring Props
  const { id } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('admin/job');
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  const { filter, needRefreshData, openAlertDelete, selectedIds, menuSelectedId } = useAppSelector(
    (state) => state.customer
  );
  //#endregion Selector

  //#region Declare State
  const [openEditCustomerDialog, setOpenEditCustomerDialog] = useState<boolean>(false);
  const [editContactDialogMode, setEditContactDialogMode] = useState<string>('');
  const [contact, setContact] = useState<ICustomer>();
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    dispatch(handleGetContacts({ ...filter, jobId: id }))
      .unwrap()
      .catch((e: any) => {
        handleApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, needRefreshData]);
  //#endregion Implement Hook

  //#region Handle Function

  const handleOpenCreateCustomer = async () => {
    setEditContactDialogMode(DIALOG_MODE_CREATE);
    setOpenEditCustomerDialog(true);
  };

  const handleOpenEditCustomer = async (id: string) => {
    try {
      const response = await dispatch(handleGetDetail(id)).unwrap(); // Assuming you use the same API for contacts
      setContact(response);
      setEditContactDialogMode(DIALOG_MODE_EDIT);
      setOpenEditCustomerDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleSetOpenEditCustomer = (open: boolean, needRefreshData?: boolean) => {
    setOpenEditCustomerDialog(open);
    needRefreshData && dispatch(customerActions.changeNeedRefreshData(needRefreshData));
    if (!open) setContact(undefined);
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(customerActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const onDeleteSelected = useCallback(async () => {
    let payloadDeleteCustomers: IPayloadDelete =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await jobApi.deleteJobCustomer(Number(id), payloadDeleteCustomers);
      notify('success', t('deleteSuccessMessage'));
      dispatch(customerActions.changeNeedRefreshData(true));
      dispatch(customerActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, id, menuSelectedId, selectedIds, t]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(customerActions.changeMenuSelectId(undefined));
    dispatch(customerActions.changeOpenAlertDelete(false));
  }, [dispatch]);
  //#endregion Handle Function

  return (
    <>
      <Box className='flex'>
        {permissions[PERMISSIONS.contact_delete] && (
          <Button
            className={cx(
              { 'visibility: visible': selectedIds.length > 0 },
              { 'visibility: hidden': selectedIds.length <= 0 }
            )}
            onClick={openAlertDeleteModal}
            variant='contained'
            color='error'
            disableElevation
            startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
          >
            {t('delete')}
          </Button>
        )}
        <Button
          className='ml-auto'
          variant='contained'
          disableElevation
          startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
          onClick={handleOpenCreateCustomer}
        >
          {t('addNew')}
        </Button>
      </Box>
      <ContactList onOpenEdit={handleOpenEditCustomer} />;
      <EditCustomerDialog
        open={openEditCustomerDialog}
        mode={editContactDialogMode}
        setOpen={handleSetOpenEditCustomer}
        data={contact}
        addExistMode={!contact ? true : undefined}
        jobId={id}
      />
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescriptionDeleteContact')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelected}
      />
    </>
  );
};

export default memo(Contact);
